import { buildLedgerGraph } from "@/zapi-frontend/shared/lib/rqaSim/engine";
import type { TLedgerFigure } from "@/zapi-frontend/shared/types/modelLedger";
// The very file the generation runs on the runner. Inlined into the download rather than
// re-implemented here: a fourth copy of the grammar was the loosest of the four, evaluating what
// the ledger refuses and refusing what it evaluates, and a downloaded model has to compute what
// the ledger computed or it is not the model.
import ledgerSource from "../../../../../../claude-runner/skills/project-generation/lib/ledger_helper.py?raw";

// JSON string literals are valid Python string literals, which is what keeps the quoting simple.
const py = (text: string | null | undefined) => JSON.stringify(text ?? "");

const decimalsOf = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) return 2;
  const text = String(value);
  const dot = text.indexOf(".");
  return dot < 0 ? 0 : Math.min(6, text.length - dot - 1);
};

// A source as `ledger_helper.py` reads one: the structured dict where the ledger has it, otherwise the
// free text an older ledger carried, which `Manifest._source` accepts as a plain string.
const pySource = (figure: TLedgerFigure): string => {
  const source = figure.source;
  if (!source) return py("");
  if (!source.kind) return py(source.ref ?? source.title ?? "");
  const fields: string[] = [`"kind": ${py(source.kind)}`];
  if (source.ref) fields.push(`"ref": ${py(source.ref)}`);
  if (source.url) fields.push(`"url": ${py(source.url)}`);
  if (source.title) fields.push(`"title": ${py(source.title)}`);
  return `{${fields.join(", ")}}`;
};

// The body of `ledger_helper.py` without its module docstring, which describes the file it came from and
// would read as this model's own.
const ledgerBody = () => ledgerSource.replace(/^"""[\s\S]*?"""\s*/, "");

export const buildModelPy = (figures: TLedgerFigure[], projectName: string): string => {
  const ranged = figures.filter((f) => f.kind === "stated" && f.range && f.value !== null);
  const lines: string[] = [];
  lines.push("#!/usr/bin/env python3");
  lines.push('"""');
  // Escaped like every other string here: a name carrying a triple quote would otherwise close
  // this docstring, and whatever followed would run when someone executes the file they downloaded.
  lines.push(`model.py — ${py(projectName).slice(1, -1)}`);
  lines.push("");
  lines.push("The working model of this case, exported from its Model Ledger. Every figure is");
  lines.push("declared once: an input with its source, a computed figure with its formula, or a");
  lines.push("relation that must hold. build() takes the swept inputs as a dict, so a sensitivity");
  lines.push("is another call of build() at another value rather than a hand-edited line.");
  lines.push("");
  lines.push("The Manifest below is the one the platform runs, verbatim: this file computes what the");
  lines.push("ledger computed, refuses what it refused, and rounds as it rounded.");
  lines.push("");
  lines.push("Run:  python3 model.py            prints every figure and writes ledger.json beside it;");
  lines.push("                                  exits non-zero when a relation does not hold");
  lines.push('"""');
  lines.push("");
  lines.push(ledgerBody());
  lines.push("");
  lines.push("# Inputs the case turns on, with the range each may take.");
  lines.push("BASE = {");
  for (const figure of ranged) {
    const range = figure.range as NonNullable<TLedgerFigure["range"]>;
    lines.push(`    ${py(figure.key)}: ${figure.value},  # ${range.min} to ${range.max}, step ${range.step}`);
  }
  lines.push("}");
  lines.push("");
  lines.push("");
  lines.push("def build(p):");
  lines.push("    m = Manifest()");
  // In evaluation order: a figure must be declared before the one whose formula reads it, and the
  // ledger is stored in the order it was written. Relations come last, once everything they compare
  // exists. Anything the graph does not order (a cycle) keeps its place rather than disappearing.
  const order = new Map(buildLedgerGraph(figures).order.map((key, index) => [key, index]));
  const ordered = [...figures].sort((a, b) => {
    if ((a.kind === "relation") !== (b.kind === "relation")) return a.kind === "relation" ? 1 : -1;
    return (order.get(a.key) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.key) ?? Number.MAX_SAFE_INTEGER);
  });

  let group: string | null = null;
  for (const figure of ordered) {
    if (figure.group !== group) {
      group = figure.group;
      lines.push("");
      lines.push(`    m.group(${py(group)})`);
    }
    const dp = typeof figure.dp === "number" ? figure.dp : decimalsOf(figure.value);
    const named = figure.name && figure.name !== figure.key ? `, name=${py(figure.name)}` : "";
    const described = figure.description ? `, description=${py(figure.description)}` : "";
    const cast = figure.role ? `, role=${py(figure.role)}` : "";
    if (figure.kind === "relation" && figure.formula) {
      lines.push(`    m.rel(${py(figure.key)}, ${py(figure.formula)}${named}${described})`);
    } else if (figure.kind === "computed" && figure.formula) {
      lines.push(
        `    m.calc(${py(figure.key)}, ${py(figure.formula)}, ${py(figure.unit)}${named}${described}${cast}, dp=${dp})`
      );
    } else if (figure.value !== null) {
      const value = figure.range ? `p[${py(figure.key)}]` : String(figure.value);
      const range = figure.range ? `, min=${figure.range.min}, max=${figure.range.max}, step=${figure.range.step}` : "";
      lines.push(
        `    m.inp(${py(figure.key)}, ${value}, ${py(figure.unit)}, source=${pySource(
          figure
        )}${named}${described}${cast}, dp=${dp}${range}${classified(figure)})`
      );
    }
  }
  lines.push("    return m");
  lines.push("");
  lines.push("");
  lines.push('if __name__ == "__main__":');
  lines.push("    run(build, BASE)");
  lines.push("");
  return lines.join("\n");
};

const pyKeys = (keys: string[]) => `[${keys.map(py).join(", ")}]`;

const classified = (figure: TLedgerFigure) =>
  [
    figure.provenance && figure.provenance !== "computed" ? `provenance=${py(figure.provenance)}` : null,
    figure.confidence ? `confidence=${py(figure.confidence)}` : null,
    figure.binding ? `binding=${py(figure.binding)}` : null,
    figure.treatment ? `treatment=${py(figure.treatment)}` : null,
    figure.asOf ? `as_of=${py(figure.asOf)}` : null,
    figure.assumedFrom?.length ? `assumed_from=${pyKeys(figure.assumedFrom)}` : null,
    figure.relatedTo?.length ? `related_to=${pyKeys(figure.relatedTo)}` : null,
    figure.dependsOn.length ? `depends_on=${pyKeys(figure.dependsOn)}` : null
  ]
    .filter(Boolean)
    .map((arg) => `, ${arg}`)
    .join("");

export const modelPyFileName = (projectName: string) =>
  `${
    projectName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "") || "model"
  }-model.py`;
